import { inject, Injectable } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { SplashScreen } from '@capacitor/splash-screen';
import { Platform } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs';

import { SessionStorageService } from '@ninety/mobile/platform/services';

import { SessionActions } from '../app-global/session/session.actions';
import { capacitorAppActions } from '../capacitor/app/capacitor-app.actions';

import { appActions } from './app.actions';
import { AppSelectors } from './app.selectors';

@Injectable()
export class AppEffects {
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly platform = inject(Platform);
  private readonly store = inject(Store);
  private readonly storage = inject(SessionStorageService);

  /**
   * Where the reload from the latest company switch lands, if not the current page.
   *
   * Held here rather than written to `reloadUrl` up front: the switch can still be refused, and a
   * refused switch must not leave a destination behind for some later reload to replay. Every switch
   * sets it, so a refused one is simply overwritten by the next — and it dies with the reload.
   */
  private switchReloadUrl?: string;

  chunkLoadError$ = createEffect(
    () =>
      this.router.events.pipe(
        filter(evt => evt instanceof NavigationError),
        map(evt => evt as NavigationError),
        tap(evt => {
          // This error occurs when a newer version of the app has been published
          // the old lazy loaded chunk is not available anymore on the server
          // which results in a ChunkLoadError
          if (evt.error instanceof Error && evt.error.name == 'ChunkLoadError') {
            // Reload the page which will cause the latest JS to be fetched
            window.location.assign(evt.url);
          }
        })
      ),
    { dispatch: false }
  );

  // Ordered rather than conflated: `signedOut` says what happened, this decides what the app does
  // about it. Anything holding an identity listens to the action, not to the reset.
  resetStateOnSignOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.signedOut),
      map(() => appActions.resetGlobalState())
    )
  );

  rememberSwitchReloadUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.switchCompany),
        tap(({ reloadUrl }) => (this.switchReloadUrl = reloadUrl))
      ),
    { dispatch: false }
  );

  reloadApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.reloadApp),
        tap(() => {
          this.showSplashAndReload();
        })
      ),
    { dispatch: false }
  );

  /**
   * The app leaving the foreground, as an action.
   *
   * `appStateChanged` rather than `appResumed`, which only answers the coming-back half. It carries the
   * direction, so this filters to the half anything acts on. `CapacitorAppEffects` owns the listener.
   */
  appBackgrounded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(capacitorAppActions.appStateChanged),
      filter(({ isActive }) => !isActive),
      map(() => appActions.appBackgrounded())
    )
  );

  appIsReady$ = createEffect(
    () =>
      this.store.select(AppSelectors.appReady).pipe(
        filter(ready => ready === true),
        map(() => {
          console.log('App is ready');
          // eslint-disable-next-line @ngrx/no-dispatch-in-effects
          this.store.dispatch(appActions.appReady());
          if (this.platform.is('capacitor')) {
            // Hide the splash screen after the app is ready
            SplashScreen.hide();
          }
        })
      ),
    { dispatch: false }
  );

  appAndCompanyReady$ = createEffect(
    () =>
      this.store.select(AppSelectors.appAndCompanyReady).pipe(
        filter(ready => ready === true),
        map(() => {
          console.log('App and Company are ready');
          // eslint-disable-next-line @ngrx/no-dispatch-in-effects
          this.store.dispatch(appActions.appAndCompanyReady());

          const url = this.storage.get('reloadUrl');
          if (url) {
            this.router.navigateByUrl(url);
            this.storage.delete('reloadUrl');
          }
        })
      ),
    { dispatch: false }
  );

  openDesktopApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.openDesktopApp),
        tap(async action => {
          const desktopUrl = action.url ?? 'https://app.ninety.io';
          if (this.platform.is('capacitor')) {
            await Browser.open({ url: desktopUrl, windowName: '_blank' });
            return;
          }
          window.open(desktopUrl, '_blank')?.focus();
        })
      ),
    { dispatch: false }
  );

  private showSplashAndReload() {
    this.storage.set('reloadUrl', this.switchReloadUrl ?? this.router.url);
    if (this.platform.is('capacitor')) {
      SplashScreen.show({
        showDuration: 500,
        autoHide: false,
      }).finally(() => {
        setTimeout(() => window.location.reload(), 100);
      });
    } else {
      window.location.reload();
    }
  }
}
